import { Injectable } from '@angular/core';
import { AlertFeedback, AlertVerdict } from 'app/shared/models/alert-feedback.model';
import { WaitingAlert } from 'app/shared/models/waiting-alert.model';
import { BehaviorSubject, Observable, delay, of } from 'rxjs';
import { NotificationsService } from '../notifications/notifications.service';
import { buildWaitingAlertsFixture } from './waiting-alerts.fixture';

/**
 * Owns the anomalies waiting for review and the verdicts recorded against them.
 *
 * It is the only writer of `NotificationsService.setUnreadCount`, so the rail badge and the tray's
 * count cannot drift apart — they are the same number read twice.
 *
 * There is no endpoint yet: `fetchWaitingAlerts` is the single seam, and swapping it for the real
 * request is the whole of that ticket's work here.
 */
@Injectable({
  providedIn: 'root'
})
export class AlertsService {
  private readonly _waitingAlerts = new BehaviorSubject<WaitingAlert[]>([]);

  /** Anomalies still awaiting a verdict, newest first. */
  public readonly waitingAlerts$: Observable<WaitingAlert[]> = this._waitingAlerts.asObservable();

  /**
   * Verdicts recorded this session, keyed by alert id.
   *
   * A reviewed alert leaves `waitingAlerts$` immediately so the count drops, but the card it was
   * rendered in keeps its place and shows what was decided instead of vanishing under the cursor.
   * The next load is what clears it.
   */
  private readonly _feedbackByAlertId = new Map<string, AlertFeedback>();

  constructor(private readonly _notificationsService: NotificationsService) {}

  /** Loads the waiting anomalies and publishes their count to the rail badge. */
  loadWaitingAlerts(): void {
    this.fetchWaitingAlerts().subscribe(alerts => {
      this._feedbackByAlertId.clear();
      this.publishWaitingAlerts(this.sortNewestFirst(alerts));
    });
  }

  /** Records a verdict, drops the alert from the waiting list and updates the badge. */
  recordFeedback(alertId: string, verdict: AlertVerdict, reason?: string): AlertFeedback {
    const feedback: AlertFeedback = {
      alertId,
      verdict,
      reason: this.normalizeReason(verdict, reason),
      recordedAt: new Date()
    };

    this._feedbackByAlertId.set(alertId, feedback);
    this.publishWaitingAlerts(this._waitingAlerts.value.filter(alert => alert.id !== alertId));

    return feedback;
  }

  /** The verdict recorded against an alert this session, if there is one. */
  feedbackFor(alertId: string): AlertFeedback | undefined {
    return this._feedbackByAlertId.get(alertId);
  }

  /** Current waiting anomalies without subscribing. */
  getWaitingAlerts(): WaitingAlert[] {
    return this._waitingAlerts.value;
  }

  /**
   * The one place the alerts come from.
   *
   * Replace this body with the HTTP call when the endpoint lands; nothing else in the tray reads
   * the fixture.
   *
   * **`delay(0)` is not decoration.** A stub that emits synchronously is not a stand-in for a
   * request — it produces timing the endpoint never will, and that difference was a real bug. The
   * tray calls `loadWaitingAlerts()` from its `ngOnInit`, so a synchronous emission ran
   * `setUnreadCount` *inside* the change-detection pass that was still rendering the shell. The rail
   * sits before the tray in `gains-layout`, so its badge had already been checked at 0 by then, and
   * Angular's dev-mode verification found 3 — `NG0100 ExpressionChangedAfterItHasBeenCheckedError`
   * on every cold start. Deferring by a macrotask puts the write after the pass, exactly where the
   * HTTP response will land. The badge is therefore 0 for one tick before it fills, which is what
   * the real endpoint will do too.
   */
  private fetchWaitingAlerts(): Observable<WaitingAlert[]> {
    return of(buildWaitingAlertsFixture()).pipe(delay(0));
  }

  private publishWaitingAlerts(alerts: WaitingAlert[]): void {
    this._waitingAlerts.next(alerts);
    this.publishUnreadCount(alerts.length);
  }

  private publishUnreadCount(waitingCount: number): void {
    if (waitingCount === 0) {
      this._notificationsService.markAllReviewed();
      return;
    }

    this._notificationsService.setUnreadCount(waitingCount);
  }

  private sortNewestFirst(alerts: WaitingAlert[]): WaitingAlert[] {
    return [...alerts].sort((first, second) => second.raisedAt.getTime() - first.raisedAt.getTime());
  }

  /** A reason belongs only to a dismissal, and an empty one is no reason at all. */
  private normalizeReason(verdict: AlertVerdict, reason?: string): string | undefined {
    if (verdict !== 'not-an-issue') {
      return undefined;
    }

    return reason?.trim() || undefined;
  }
}
